import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { SNACKBAR_CLOSE } from '../utils/snackbar';

const DB_CONNECTION = 'Username-Password-Authentication';
// Until when the link waits (epoch ms), shared by every tab of this browser and kept across
// reloads.
const STORAGE_KEY = 'passwordResetWaitUntil';

/**
 * "Passwort ändern" (CIAM-420): Auth0 mails its own reset link, which opens Auth0's
 * reset-password page. The endpoint is public (no token) and answers the same for unknown
 * addresses.
 *
 * Every click would send another mail, so after one the link waits a minute (COOLDOWN_MS).
 * The link stays clickable: a click during the wait sends nothing and says how long is left
 * (account.resetLinkWait). The wait is kept in localStorage and read on every click, so a
 * reload keeps it and other open tabs respect it.
 *
 * Auth0 rate-limits the endpoint itself and sends X-RateLimit-Limit, -Remaining and -Reset
 * (exposed to the browser through CORS); over the limit it answers 429. Then the link waits
 * until Auth0's reset (Retry-After, else X-RateLimit-Reset) and the user is told how long.
 * Any other failure the browser can read ends the wait, so the user can retry; one it cannot
 * read (status 0) keeps the minute.
 */
@Injectable({ providedIn: 'root' })
export class PasswordResetService {
  static readonly COOLDOWN_MS = 60_000;
  // Upper bound for any wait, also for a stored or announced time far in the future
  // (clock change, odd header), so a wait can never last long.
  static readonly MAX_WAIT_MS = 60 * 60_000;

  // The wait of this tab, for when localStorage is blocked.
  private until = 0;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  sendResetLink(email: string | undefined) {
    if (!email) {
      return;
    }
    const left = this.remaining();
    if (left > 0) {
      this.notify('account.resetLinkWait', 'info-snackbar', { time: this.duration(left) });
      return;
    }
    this.waitFor(PasswordResetService.COOLDOWN_MS);
    this.http
      .post(
        `https://${environment.auth.domain}/dbconnections/change_password`,
        { client_id: environment.auth.clientId, email, connection: DB_CONNECTION },
        { responseType: 'text' }
      )
      .subscribe({
        next: () => this.notify('account.resetLinkSent', 'success-snackbar'),
        error: (error: HttpErrorResponse) => {
          if (error.status === 429) {
            const ms = this.rateLimitWait(error);
            this.waitFor(ms);
            this.notify('account.resetLinkTooMany', 'error-snackbar', {
              minutes: Math.ceil(ms / 60_000),
            });
            return;
          }
          // Status 0: the browser got no readable answer (network, or a response without
          // CORS headers). The mail may have gone out, so the minute's wait stays.
          if (error.status !== 0) {
            this.waitFor(0);
          }
          this.notify('account.resetLinkFailed', 'error-snackbar');
        },
      });
  }

  // How long Auth0 asks to wait: Retry-After (seconds) if sent, else X-RateLimit-Reset
  // (epoch seconds), else a minute; at least a minute, at most MAX_WAIT_MS.
  private rateLimitWait(error: HttpErrorResponse): number {
    const retryAfter = Number(error.headers?.get('Retry-After'));
    const reset = Number(error.headers?.get('X-RateLimit-Reset'));
    let ms = PasswordResetService.COOLDOWN_MS;
    if (retryAfter > 0) {
      ms = retryAfter * 1000;
    } else if (reset > 0) {
      ms = reset * 1000 - Date.now();
    }
    return Math.min(Math.max(ms, PasswordResetService.COOLDOWN_MS), PasswordResetService.MAX_WAIT_MS);
  }

  // "45 Sekunden" under a minute, otherwise whole minutes rounded up ("1 Minute", "3 Minuten").
  private duration(ms: number): string {
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) {
      return this.translate.instant(seconds === 1 ? 'account.second' : 'account.seconds', { n: seconds });
    }
    const minutes = Math.ceil(seconds / 60);
    return this.translate.instant(minutes === 1 ? 'account.minute' : 'account.minutes', { n: minutes });
  }

  // Starts (ms > 0) or ends (0) the wait, for this tab and the others.
  private waitFor(ms: number) {
    this.until = ms > 0 ? Date.now() + ms : 0;
    try {
      if (ms > 0) {
        localStorage.setItem(STORAGE_KEY, String(this.until));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage blocked (private mode): the wait holds in this tab until a reload.
    }
  }

  // Milliseconds left of the wait; 0 when none, or when it is further away than MAX_WAIT_MS.
  private remaining(): number {
    let until = this.until;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      until = stored === null ? 0 : Number(stored) || 0;
    } catch {
      // Storage blocked: only this tab's own wait counts.
    }
    const left = until - Date.now();
    return left > 0 && left <= PasswordResetService.MAX_WAIT_MS ? left : 0;
  }

  private notify(key: string, panelClass: string, params?: Record<string, unknown>) {
    this.snackBar.open(this.translate.instant(key, params), SNACKBAR_CLOSE, {
      duration: 5000,
      verticalPosition: 'top',
      politeness: 'assertive',
      panelClass: [panelClass],
    });
  }
}
